import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Check, ChevronDown, Trash2, AlertTriangle } from 'lucide-react';
import { PageHeader, SectionTitle } from './PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { addUserEmail, forgetUserEmail, getCurrentUser, makePrimaryUserEmail, updateCurrentUser, verifyUserEmail, verifyUserPhone } from '@/api/beatlyApi';
import { getTokenFromStorage, updateAuthInStorage, logout as hardLogout } from '@/utils/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

interface SettingsPageProps {
  userName: string;
}

const languageOptions = [
  { code: 'en-US', label: 'English (US)', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'en-GB', label: 'English (UK)', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'sv-SE', label: 'Svenska', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'de-DE', label: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'fr-FR', label: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'es-ES', label: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'ja-JP', label: '\u65E5\u672C\u8A9E', flag: '\u{1F1EF}\u{1F1F5}' },
];

export const SettingsPage: React.FC<SettingsPageProps> = ({ userName }) => {
  const { user, setUser, logout } = useAuth();
  const effectiveLogout = logout ?? hardLogout;
  const userEditedRef = useRef(false);
  const prevUserIdRef = useRef<string | undefined>(undefined);
  const phoneOtpRefs = useRef<Array<HTMLInputElement | null>>([]);

  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [language, setLanguage] = useState('en-US');
  const [phone, setPhone] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);
  const pendingWantEmailNotificationsRef = useRef<boolean | null>(null);
  const [emails, setEmails] = useState<Array<{ address: string; primary: boolean; verified: boolean }>>([]);
  const [isAddEmailOpen, setIsAddEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addEmailError, setAddEmailError] = useState<string | null>(null);
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [isDeletingEmail, setIsDeletingEmail] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState<string | null>(null);
  const [isMakingPrimaryEmail, setIsMakingPrimaryEmail] = useState(false);
  const [makingPrimaryEmail, setMakingPrimaryEmail] = useState<string | null>(null);
  const [isVerifyPhoneOpen, setIsVerifyPhoneOpen] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState<string[]>(['', '', '', '']);
  const [verifyPhoneError, setVerifyPhoneError] = useState<string | null>(null);
  const [isRequestingPhoneVerification, setIsRequestingPhoneVerification] = useState(false);
  const [isVerifyingPhoneOtp, setIsVerifyingPhoneOtp] = useState(false);

  const derivedUserName = useMemo(() => {
    const first = typeof user?.first_name === 'string' ? user.first_name.trim() : '';
    const last = typeof user?.last_name === 'string' ? user.last_name.trim() : '';
    const full = `${first} ${last}`.trim();
    if (full) return full;
    const name = typeof user?.name === 'string' ? user.name.trim() : '';
    return name || userName;
  }, [user?.first_name, user?.last_name, user?.name, userName]);

  const isPhoneVerified = Boolean(user?.phone_is_verified) && phone.trim() === String(user?.phone ?? '').trim();

  const mapApiLanguageToUi = (apiLang: string | undefined): string => {
    const base = (apiLang ?? '').trim().toLowerCase();
    if (!base) return 'en-US';
    if (base.startsWith('en')) return 'en-US';
    if (base.startsWith('sv')) return 'sv-SE';
    if (base.startsWith('de')) return 'de-DE';
    if (base.startsWith('fr')) return 'fr-FR';
    if (base.startsWith('es')) return 'es-ES';
    if (base.startsWith('ja')) return 'ja-JP';
    return 'en-US';
  };

  const mapUiLanguageToApi = (uiLang: string): string => {
    const base = (uiLang ?? '').trim();
    if (!base) return 'en';
    return base.split('-')[0]?.toLowerCase() || 'en';
  };

  const isAuthUser = (val: unknown): val is { user_id: string } => {
    return Boolean(
      val &&
        typeof val === 'object' &&
        'user_id' in val &&
        typeof (val as { user_id?: unknown }).user_id === 'string' &&
        (val as { user_id: string }).user_id.trim().length > 0
    );
  };

  useEffect(() => {
    const currentUserId = typeof user?.user_id === 'string' ? user.user_id : undefined;
    const userIdUnchanged = prevUserIdRef.current === currentUserId;
    if (userEditedRef.current && userIdUnchanged) return;

    const fullName = derivedUserName;
    const nameParts = fullName.split(' ').filter(Boolean);
    const initialFirst = typeof user?.first_name === 'string' && user.first_name ? user.first_name : (nameParts[0] || '');
    const initialLast =
      typeof user?.last_name === 'string' && user.last_name
        ? user.last_name
        : (nameParts.slice(1).join(' ') || '');

    setFirstName(initialFirst);
    setSurname(initialLast);
    setPhone(typeof user?.phone === 'string' ? user.phone : '');
    setLanguage(mapApiLanguageToUi(typeof user?.language === 'string' ? user.language : undefined));
    setEmailNotifications(Boolean(user?.want_email_notifications ?? true));

    const u = user as Record<string, unknown> | undefined;
    const apiEmails = u && Array.isArray(u.emails) ? (u.emails as unknown[]) : null;
    if (apiEmails && apiEmails.length > 0) {
      const parsed = apiEmails
        .map((e) => {
          if (!e || typeof e !== 'object') return null;
          const obj = e as Record<string, unknown>;
          const address = typeof obj.address === 'string' ? obj.address : (typeof obj.email === 'string' ? obj.email : '');
          if (!address) return null;
          return {
            address,
            primary: Boolean(obj.is_primary ?? obj.primary ?? false),
            verified: Boolean(obj.is_verified ?? obj.verified ?? false),
          };
        })
        .filter(Boolean) as Array<{ address: string; primary: boolean; verified: boolean }>;

      if (parsed.length > 0) {
        const sorted = [...parsed];
        const primaryIndex = sorted.findIndex((e) => e.primary);
        if (primaryIndex > 0) {
          const [primaryEmail] = sorted.splice(primaryIndex, 1);
          sorted.unshift(primaryEmail);
        }
        setEmails(sorted);
        return;
      }
    }

    const primary = typeof user?.email === 'string' ? user.email : '';
    if (primary) {
      setEmails([{ address: primary, primary: true, verified: Boolean((user as Record<string, unknown> | undefined)?.email_is_verified ?? true) }]);
    } else {
      setEmails([]);
    }
    prevUserIdRef.current = currentUserId;
    userEditedRef.current = false;
  }, [
    derivedUserName,
    user?.user_id,
    user?.first_name,
    user?.last_name,
    user?.phone,
    user?.language,
    user?.want_email_notifications,
    user?.email,
    (user as Record<string, unknown> | undefined)?.emails,
    (user as Record<string, unknown> | undefined)?.email_is_verified,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const token = getTokenFromStorage();
      if (!token) return;
      try {
        const refreshed = await getCurrentUser(token);
        if (cancelled) return;
        updateAuthInStorage({ token, user: refreshed });
        setUser?.(refreshed);
      } catch {
        // Ignore; AuthInitializer handles invalid tokens globally.
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [setUser]);

  const selectedLang = languageOptions.find((l) => l.code === language) || languageOptions[0];

  const handleSave = async () => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }

    setIsSaving(true);
    try {
      const res = await updateCurrentUser(token, {
        first_name: firstName,
        last_name: surname,
        phone,
        language: mapUiLanguageToApi(language),
        want_email_notifications: emailNotifications,
        send_phone_verification_sms: false,
      });

      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        // Keep UI as-is; backend uses codes for toast messaging in web-app.
        return;
      }

      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleNotifications = async () => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }
    if (isUpdatingNotifications) return;

    setIsUpdatingNotifications(true);
    const newValue = !emailNotifications;
    setEmailNotifications(newValue);
    pendingWantEmailNotificationsRef.current = newValue;

    try {
      const res = await updateCurrentUser(token, { want_email_notifications: newValue });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        // No user update payload; keep optimistic UI, but end loading state.
        pendingWantEmailNotificationsRef.current = null;
        return;
      }

      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }
      pendingWantEmailNotificationsRef.current = null;
    } catch {
      // Best-effort: revert UI if request fails.
      setEmailNotifications(!newValue);
      pendingWantEmailNotificationsRef.current = null;
    } finally {
      setIsUpdatingNotifications(false);
    }
  };

  const focusPhoneOtpIndex = (index: number) => {
    const el = phoneOtpRefs.current[index];
    if (el) el.focus();
  };

  const handleRequestPhoneVerification = async () => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }
    const normalizedPhone = phone.trim();
    if (!normalizedPhone) return;
    if (isRequestingPhoneVerification) return;

    setIsRequestingPhoneVerification(true);
    setVerifyPhoneError(null);
    try {
      const res = await updateCurrentUser(token, { phone: normalizedPhone, send_phone_verification_sms: true });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        setVerifyPhoneError('Could not send verification code. Please try again.');
        return;
      }
      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }

      setPhoneOtp(['', '', '', '']);
      setIsVerifyPhoneOpen(true);
      setTimeout(() => focusPhoneOtpIndex(0), 0);
    } catch {
      setVerifyPhoneError('Could not send verification code. Please try again.');
    } finally {
      setIsRequestingPhoneVerification(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }
    if (isVerifyingPhoneOtp) return;

    const otp = phoneOtp.join('').trim();
    if (otp.length !== 4) {
      setVerifyPhoneError('Please enter the 4-character code.');
      return;
    }

    setIsVerifyingPhoneOtp(true);
    setVerifyPhoneError(null);
    try {
      const res = await verifyUserPhone(token, { otp });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        setVerifyPhoneError('Invalid code. Please try again.');
        return;
      }
      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }

      setIsVerifyPhoneOpen(false);
      setPhoneOtp(['', '', '', '']);
    } catch {
      setVerifyPhoneError('Invalid code. Please try again.');
    } finally {
      setIsVerifyingPhoneOtp(false);
    }
  };

  const handlePhoneOtpChange = (index: number, raw: string) => {
    const char = (raw ?? '').slice(-1);
    if (!char) {
      setPhoneOtp((prev) => {
        const next = [...prev];
        next[index] = '';
        return next;
      });
      return;
    }

    if (!/^[a-zA-Z0-9]$/.test(char)) return;
    const normalized = char.toUpperCase();
    setPhoneOtp((prev) => {
      const next = [...prev];
      next[index] = normalized;
      return next;
    });

    if (index < 3) {
      focusPhoneOtpIndex(index + 1);
    }
  };

  const handlePhoneOtpKeyDown = (index: number, key: string) => {
    if (key !== 'Backspace') return;
    setPhoneOtp((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = '';
        return next;
      }
      if (index > 0) {
        next[index - 1] = '';
        setTimeout(() => focusPhoneOtpIndex(index - 1), 0);
      }
      return next;
    });
  };

  const isValidEmail = (s: string) => {
    const v = (s ?? '').trim();
    if (!v) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const handleAddEmail = async () => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }

    const email = newEmail.trim();
    if (!isValidEmail(email)) {
      setAddEmailError('Please enter a valid email address.');
      return;
    }

    setIsAddingEmail(true);
    setAddEmailError(null);
    try {
      const res = await addUserEmail(token, { email });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        // Backend signaled a state (eg already registered). Keep UI, show generic error.
        setAddEmailError('Could not add email. Please try a different address.');
        return;
      }
      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }

      setIsAddEmailOpen(false);
      setNewEmail('');
      setAddEmailError(null);
    } catch {
      setAddEmailError('Could not add email. Please try again.');
    } finally {
      setIsAddingEmail(false);
    }
  };

  const handleDeleteEmail = async (email: string) => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }
    if (isDeletingEmail) return;

    setIsDeletingEmail(true);
    setDeletingEmail(email);
    try {
      const res = await forgetUserEmail(token, { email });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        return;
      }
      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }
    } finally {
      setDeletingEmail(null);
      setIsDeletingEmail(false);
    }
  };

  const handleVerifyEmail = async (email: string) => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }
    if (isVerifyingEmail) return;

    setIsVerifyingEmail(true);
    setVerifyingEmail(email);
    try {
      const res = await verifyUserEmail(token, { email });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        return;
      }
      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }
    } finally {
      setVerifyingEmail(null);
      setIsVerifyingEmail(false);
    }
  };

  const handleMakePrimaryEmail = async (email: string) => {
    const token = getTokenFromStorage();
    if (!token) {
      effectiveLogout();
      return;
    }
    if (isMakingPrimaryEmail) return;

    setIsMakingPrimaryEmail(true);
    setMakingPrimaryEmail(email);
    try {
      const res = await makePrimaryUserEmail(token, { email });
      if (res && typeof res === 'object' && 'isExpired' in res && (res as { isExpired?: boolean }).isExpired) {
        effectiveLogout();
        return;
      }
      if (res && typeof res === 'object' && 'code' in res) {
        return;
      }
      if (isAuthUser(res)) {
        updateAuthInStorage({ token, user: res });
        setUser?.(res as never);
      }
    } finally {
      setMakingPrimaryEmail(null);
      setIsMakingPrimaryEmail(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto p-6 lg:p-8 bg-[#f8f9fa] min-h-screen">
      {/* ── Branded Page Header ── */}
      <PageHeader
        category="ACCOUNT"
        title="Settings"
        subtitle="Manage your profile, login preferences, and notification settings."
      />

      {/* Settings Sections */}
      <div className="space-y-0">

        {/* ── Login Email ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-8 border-b border-gray-100">
          <div className="lg:col-span-4">
            <SectionTitle
              title="Login Email"
              subtitle="Link multiple email addresses to your account for login. All official communications will be sent to the primary email address."
            />
          </div>
          <div className="lg:col-span-8">
            <div className="w-full max-w-lg">
              <label className="text-[#344054] text-xs mb-2 flex items-center gap-1.5">
                Primary Email *
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="w-3.5 h-3.5 rounded-full border border-gray-300 flex items-center justify-center text-[8px] text-gray-400 cursor-help"
                      role="img"
                      aria-label="Primary email info"
                      tabIndex={0}
                    >
                      ?
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={10}
                    className="bg-[#101828] text-white rounded-xl px-4 py-2 text-[13px] shadow-[0_12px_30px_rgba(16,24,40,0.28)]"
                  >
                    Used as the main email for communication
                  </TooltipContent>
                </Tooltip>
              </label>
            </div>
            {emails.map((email, i) => {
              const showVerify = !email.primary && !email.verified;
              const showMakePrimary = !email.primary && email.verified;

              return (
                <div key={i} className="flex items-center gap-2 mb-3 flex-nowrap">
                  <div className="flex items-center gap-2.5 px-4 h-12 bg-[#2e024c] rounded-xl w-full max-w-lg flex-none min-w-0">
                    {email.verified ? (
                      <div
                        className="w-5 h-5 rounded-full bg-[#a9f600]/20 flex items-center justify-center shrink-0"
                        title="Verified"
                      >
                        <Check className="w-3 h-3 text-[#a9f600]" />
                      </div>
                    ) : (
                      <div
                        className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0"
                        title="Email not verified"
                      >
                        <AlertTriangle className="w-3 h-3 text-amber-400" />
                      </div>
                    )}
                    <span className="text-white/90 text-sm truncate">{email.address}</span>
                    {!email.primary && (
                      <button
                        type="button"
                        onClick={() => handleDeleteEmail(email.address)}
                        disabled={isDeletingEmail && deletingEmail === email.address}
                        className="ml-auto p-1.5 rounded-lg text-white/60 hover:text-[#ff5635] hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Delete email"
                        title="Delete email"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="h-12 flex items-center justify-start w-36 flex-none min-w-0">
                    {showVerify ? (
                      <button
                        type="button"
                        onClick={() => handleVerifyEmail(email.address)}
                        disabled={isVerifyingEmail && verifyingEmail === email.address}
                        className="text-sm text-[#be43ff] hover:text-[#a830e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        style={{ fontWeight: 600 }}
                      >
                        Verify
                      </button>
                    ) : showMakePrimary ? (
                      <button
                        type="button"
                        onClick={() => handleMakePrimaryEmail(email.address)}
                        disabled={isMakingPrimaryEmail && makingPrimaryEmail === email.address}
                        className="text-sm text-[#be43ff] hover:text-[#a830e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                        style={{ fontWeight: 600 }}
                      >
                        Make Primary
                      </button>
                    ) : (
                      <div />
                    )}
                  </div>
                </div>
              );
            })}
            <div className="w-full max-w-lg">
              <button
                type="button"
                onClick={() => setIsAddEmailOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm text-[#be43ff] hover:text-[#a830e0] transition-colors mt-1"
              >
                <Plus className="w-4 h-4" />
                Add Email
              </button>
            </div>
          </div>
        </div>

        {/* ── General Info ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-8 border-b border-gray-100">
          <div className="lg:col-span-4">
            <SectionTitle
              title="General info"
              subtitle="Enter your basic information for effective collaboration."
            />
          </div>
          <div className="lg:col-span-8 space-y-5">
            {/* Name Fields */}
            <div className="w-full max-w-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[#344054] text-xs mb-1.5 block">First name *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      userEditedRef.current = true;
                      setFirstName(e.target.value);
                    }}
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-[#101828] text-sm placeholder:text-[#98a2b3] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="text-[#344054] text-xs mb-1.5 block">Surname *</label>
                  <input
                    type="text"
                    value={surname}
                    onChange={(e) => {
                      userEditedRef.current = true;
                      setSurname(e.target.value);
                    }}
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-[#101828] text-sm placeholder:text-[#98a2b3] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all"
                    placeholder="Surname"
                  />
                </div>
              </div>
            </div>

            {/* Language */}
            <div className="relative w-full max-w-lg">
              <label className="text-[#344054] text-xs mb-1.5 flex items-center gap-1.5">
                Language
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="w-3.5 h-3.5 rounded-full border border-gray-300 flex items-center justify-center text-[8px] text-gray-400 cursor-help"
                      role="img"
                      aria-label="Language info"
                      tabIndex={0}
                    >
                      ?
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    sideOffset={10}
                    className="bg-[#101828] text-white rounded-xl px-4 py-2 text-[13px] shadow-[0_12px_30px_rgba(16,24,40,0.28)]"
                  >
                    Language of communication
                  </TooltipContent>
                </Tooltip>
              </label>
              <button
                type="button"
                onClick={() => setLanguageOpen(!languageOpen)}
                className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-[#101828] text-sm flex items-center justify-between hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all"
              >
                <span className="flex items-center gap-2">
                  <span className="text-base">{selectedLang.flag}</span>
                  {selectedLang.label}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${languageOpen ? 'rotate-180' : ''}`} />
              </button>
              {languageOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setLanguageOpen(false)} />
                  <div className="absolute z-20 w-full mt-1.5 bg-white rounded-xl border border-gray-100 shadow-lg shadow-black/5 overflow-hidden py-1">
                    {languageOptions.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => {
                          userEditedRef.current = true;
                          setLanguage(lang.code);
                          setLanguageOpen(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                          lang.code === language ? 'text-[#be43ff] bg-[#be43ff]/5' : 'text-[#344054]'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        {lang.label}
                        {lang.code === language && <Check className="w-3.5 h-3.5 ml-auto" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <div className="w-full max-w-lg">
                <label className="text-[#344054] text-xs mb-1.5 block">Mobile number (eg. +461234567)</label>
              </div>
              <div className="flex items-center gap-2 flex-nowrap">
                <div className="relative flex-none w-full max-w-lg">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      userEditedRef.current = true;
                      setPhone(e.target.value);
                    }}
                    className={`w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-[#101828] text-sm placeholder:text-[#98a2b3] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all ${
                      phone.trim() ? 'pr-12' : ''
                    }`}
                    placeholder="Enter your phone"
                  />
                  {phone.trim() ? (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isPhoneVerified ? (
                        <div
                          className="w-5 h-5 rounded-full bg-[#a9f600]/20 flex items-center justify-center"
                          title="Verified"
                        >
                          <Check className="w-3 h-3 text-[#a9f600]" />
                        </div>
                      ) : (
                        <div
                          className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center"
                          title="Phone not verified"
                        >
                          <AlertTriangle className="w-3 h-3 text-amber-400" />
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="h-11 flex items-center justify-start w-36 flex-none min-w-0">
                  {phone.trim() && !isPhoneVerified ? (
                    <button
                      type="button"
                      onClick={handleRequestPhoneVerification}
                      disabled={isRequestingPhoneVerification}
                      className="text-sm text-[#be43ff] hover:text-[#a830e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      style={{ fontWeight: 600 }}
                    >
                      {isRequestingPhoneVerification ? 'Sending…' : 'Verify'}
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              </div>
              {verifyPhoneError && !isVerifyPhoneOpen && (
                <div className="mt-2 text-xs text-[#ff5635]" style={{ fontWeight: 500 }}>
                  {verifyPhoneError}
                </div>
              )}
            </div>

            {/* Save Button */}
            <div>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`inline-flex items-center justify-center gap-2 px-8 py-2.5 rounded-xl text-sm transition-all active:scale-[0.97] shadow-sm ${
                  saved
                    ? 'bg-[#00b1a5] text-white'
                    : 'bg-[#2e024c] text-white hover:bg-[#3a0d5e]'
                } ${isSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={{ fontWeight: 600 }}
              >
                {saved ? (
                  <>
                    <Check className="w-4 h-4" />
                    Saved
                  </>
                ) : (
                  'SAVE'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── Notifications ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 py-8 border-b border-gray-100">
          <div className="lg:col-span-4">
            <SectionTitle
              title="Notifications"
              subtitle="Opt-out of email notifications if you don't want emails for each events happening in your campaigns."
            />
          </div>
          <div className="lg:col-span-8 max-w-lg">
            <label className="flex items-center gap-3 cursor-pointer group">
              <button
                type="button"
                role="checkbox"
                aria-checked={emailNotifications}
                onClick={handleToggleNotifications}
                disabled={isUpdatingNotifications}
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${
                  emailNotifications
                    ? 'bg-[#be43ff] border-[#be43ff]'
                    : 'bg-white border-gray-300 group-hover:border-gray-400'
                }`}
              >
                {emailNotifications && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </button>
              <span className="text-sm text-[#344054]">Allow Email Notifications</span>
            </label>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 pt-8 border-t border-gray-100">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#98a2b3]">
          <span>&copy; 2026 Beatly. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Product updates</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Privacy Policy</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Terms of Service</a>
            <a href="#" className="text-[#be43ff] hover:text-[#a830e0] transition-colors">Cookie-settings</a>
          </div>
        </div>
      </footer>

      {/* Verify Phone Modal */}
      <Dialog
        open={isVerifyPhoneOpen}
        onOpenChange={(open) => {
          setIsVerifyPhoneOpen(open);
          if (!open) {
            setPhoneOtp(['', '', '', '']);
            setVerifyPhoneError(null);
          }
        }}
      >
        <DialogContent className="max-w-[480px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
              Phone verification
            </DialogTitle>
            <p className="text-[#667085] text-sm mt-1" style={{ fontWeight: 500 }}>
              Please enter the verification code sent to your phone to confirm your number.
            </p>
          </DialogHeader>
          <div className="px-6 pb-6">
            <label className="text-[#344054] text-xs mb-1.5 block">Enter OTP-code *</label>
            <div className="flex items-center gap-2">
              {phoneOtp.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    phoneOtpRefs.current[index] = el;
                  }}
                  value={value}
                  onChange={(e) => handlePhoneOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handlePhoneOtpKeyDown(index, e.key)}
                  inputMode="text"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className="w-12 h-12 rounded-xl border border-gray-200 bg-white text-[#101828] text-center text-lg focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all"
                />
              ))}
            </div>
            {verifyPhoneError && (
              <div className="mt-2 text-xs text-[#ff5635]" style={{ fontWeight: 500 }}>
                {verifyPhoneError}
              </div>
            )}

            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={handleRequestPhoneVerification}
                disabled={isRequestingPhoneVerification}
                className="text-sm text-[#be43ff] hover:text-[#a830e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 600 }}
              >
                {isRequestingPhoneVerification ? 'Sending…' : "Haven't got a code? Send a new one"}
              </button>

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsVerifyPhoneOpen(false)}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#344054] hover:bg-gray-50 transition-colors"
                  style={{ fontWeight: 600 }}
                  disabled={isVerifyingPhoneOtp}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleVerifyPhoneOtp}
                  className={`inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm text-white transition-colors ${
                    isVerifyingPhoneOtp ? 'bg-[#2e024c]/70 cursor-not-allowed' : 'bg-[#2e024c] hover:bg-[#3a0d5e]'
                  }`}
                  style={{ fontWeight: 700 }}
                  disabled={isVerifyingPhoneOtp}
                >
                  {isVerifyingPhoneOtp ? 'Verifying…' : 'Verify'}
                </button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Email Modal */}
      <Dialog open={isAddEmailOpen} onOpenChange={(open) => {
        setIsAddEmailOpen(open);
        if (!open) {
          setNewEmail('');
          setAddEmailError(null);
        }
      }}>
        <DialogContent className="max-w-[480px] rounded-2xl border border-gray-100 bg-white p-0 shadow-[0_16px_48px_rgba(16,24,40,0.16)]">
          <DialogHeader className="px-6 pt-6 pb-3">
            <DialogTitle className="text-[#101828] text-[18px]" style={{ fontWeight: 700 }}>
              Add Email
            </DialogTitle>
            <p className="text-[#667085] text-sm mt-1" style={{ fontWeight: 500 }}>
              Add an additional email address to your account. We’ll send a verification email automatically.
            </p>
          </DialogHeader>
          <div className="px-6 pb-6">
            <label className="text-[#344054] text-xs mb-1.5 block">Email address *</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (addEmailError) setAddEmailError(null);
              }}
              className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-white text-[#101828] text-sm placeholder:text-[#98a2b3] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all"
              placeholder="name@company.com"
              autoFocus
            />
            {addEmailError && (
              <div className="mt-2 text-xs text-[#ff5635]" style={{ fontWeight: 500 }}>
                {addEmailError}
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsAddEmailOpen(false)}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm text-[#344054] hover:bg-gray-50 transition-colors"
                style={{ fontWeight: 600 }}
                disabled={isAddingEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddEmail}
                className={`inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm text-white transition-colors ${
                  isAddingEmail ? 'bg-[#2e024c]/70 cursor-not-allowed' : 'bg-[#2e024c] hover:bg-[#3a0d5e]'
                }`}
                style={{ fontWeight: 700 }}
                disabled={isAddingEmail}
              >
                {isAddingEmail ? 'Adding…' : 'Add Email'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};